/**
 * @file priceSummaryBar.component.tsx
 * @path apps/website/src/features/booking/components/shared/priceSummaryBar.component.tsx
 */

import { Button } from "@sirbastians/ui";
import { formatCurrency, cn } from "@sirbastians/utils";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useState } from "react";
import { useBookingPricing } from "../hooks/useBookingPricing.hook";

interface PriceSummaryBarProps {
  onNext: () => void;
  onBack?: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextLabel?: string;
  isProcessing?: boolean;
}

export const PriceSummaryBar = ({
  onNext,
  onBack,
  isFirstStep,
  isLastStep,
  nextLabel = "Save and Continue",
  isProcessing = false,
}: PriceSummaryBarProps) => {
  const { pricing, grandTotal } = useBookingPricing();
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDraftPricing = pricing.total > 0;

  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 z-30 border-t bg-background/95 backdrop-blur-md shadow-lg",
        "transition-all duration-300",
      )}
    >
      {/* Expandable breakdown */}
      {isExpanded && hasDraftPricing && (
        <div className="border-b px-4 py-4 max-w-3xl mx-auto space-y-2 animate-in slide-in-from-bottom-2 duration-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Current Booking Breakdown
          </p>

          {pricing.servicePrice > 0 && (
            <LineItem label="Service" value={pricing.servicePrice} />
          )}
          {pricing.activitiesPrice > 0 && (
            <LineItem label="Activities" value={pricing.activitiesPrice} />
          )}
          {pricing.addOnsPrice > 0 && (
            <LineItem label="Add-ons" value={pricing.addOnsPrice} />
          )}
          {pricing.extraStopsPrice > 0 && (
            <LineItem label="Extra stops" value={pricing.extraStopsPrice} />
          )}
          {pricing.vehiclesPrice > 0 && (
            <LineItem label="VIP vehicles" value={pricing.vehiclesPrice} />
          )}

          <div className="flex justify-between text-sm font-bold pt-2 border-t">
            <span>This booking</span>
            <span>{formatCurrency(pricing.total)}</span>
          </div>

          {grandTotal > pricing.total && (
            <div className="flex justify-between text-sm text-accent font-semibold">
              <span>Grand total (all bookings)</span>
              <span>{formatCurrency(grandTotal)}</span>
            </div>
          )}
        </div>
      )}

      {/* Main bar */}
      <div className="flex items-center gap-3 px-4 py-3 max-w-3xl mx-auto">
        {/* Back button */}
        {!isFirstStep && onBack && (
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="shrink-0"
            disabled={isProcessing}
          >
            Back
          </Button>
        )}

        {/* Price toggle */}
        <button
          type="button"
          className={cn(
            "flex flex-col items-start flex-1 min-w-0 text-left rounded-lg px-2 py-1 transition-colors",
            hasDraftPricing && "hover:bg-muted cursor-pointer",
            !hasDraftPricing && "cursor-default",
          )}
          onClick={() => hasDraftPricing && setIsExpanded((v) => !v)}
          disabled={!hasDraftPricing}
          aria-expanded={isExpanded}
        >
          <span className="text-xs text-muted-foreground leading-none">
            {isLastStep ? "Grand total" : "Current booking"}
          </span>
          <span className="text-lg font-extrabold text-foreground leading-tight">
            {formatCurrency(isLastStep ? grandTotal : pricing.total)}
          </span>
        </button>

        {hasDraftPricing && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setIsExpanded((v) => !v)}
            aria-label={isExpanded ? "Collapse price breakdown" : "Expand price breakdown"}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </button>
        )}

        {/* Next / Pay button */}
        <Button
          onClick={onNext}
          disabled={isProcessing}
          className="shrink-0 min-w-[140px]"
        >
          {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {nextLabel}
        </Button>
      </div>
    </div>
  );
};

const LineItem = ({ label, value }: { label: string; value: number }) => (
  <div className="flex justify-between text-sm text-muted-foreground">
    <span>{label}</span>
    <span>{formatCurrency(value)}</span>
  </div>
);
